/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * ToolsTab (redesign)
 *
 * Replaces the old accordion-heavy Tools UI with a searchable card grid
 * where each tool row has an inline enable/disable toggle, category chips,
 * usage stats, and a side drawer for custom configuration. Assigned and
 * available tools live in a single unified list that can be filtered.
 */

import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Build as ToolIcon,
  Close as CloseIcon,
  InfoOutlined as InfoIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  FilterAltOff as ClearFilterIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { agentApi } from '../../../services/api';
import { formatStoredDateTime } from '../../../utils/datetime';
import type {
  AgentToolAssignment,
  Tool,
  ToolAssignmentRequest,
} from '../../../types';
import { SectionCard } from './primitives';
import {
  TOOL_CATEGORY_GROUP_META,
  getToolCategoryMeta,
  type ToolCategoryGroup,
  type ToolCategoryMeta,
} from './toolCategoryMeta';

interface ToolsTabProps {
  agentId: number;
}

type FilterMode = 'all' | 'assigned' | 'available' | 'enabled' | 'disabled' | 'dangerous';

interface ToolRow {
  tool: Tool;
  assignment?: AgentToolAssignment;
}

export const ToolsTab: React.FC<ToolsTabProps> = ({ agentId }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>('all');
  const [categoryMenuAnchor, setCategoryMenuAnchor] = useState<HTMLElement | null>(null);
  const [filter, setFilter] = useState<FilterMode>('all');
  const [configTool, setConfigTool] = useState<ToolRow | null>(null);
  const [configJson, setConfigJson] = useState('{}');
  const [configEnabled, setConfigEnabled] = useState(true);

  const { data, isLoading, error } = useQuery({
    queryKey: ['agent-tools', agentId],
    queryFn: () => agentApi.getTools(agentId),
    enabled: !!agentId,
  });

  const assignMutation = useMutation({
    mutationFn: (payload: ToolAssignmentRequest) => agentApi.assignTool(agentId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-tools', agentId] });
      queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Failed to update tool');
    },
  });

  const unassignMutation = useMutation({
    mutationFn: (toolName: string) => agentApi.unassignTool(agentId, toolName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-tools', agentId] });
      queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
      toast.success('Tool removed from agent');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Failed to remove tool');
    },
  });

  const rows: ToolRow[] = useMemo(() => {
    if (!data) return [];
    const byName = new Map<string, ToolRow>();
    (data.available || []).forEach((tool) => byName.set(tool.name, { tool }));
    (data.assigned || []).forEach((assignment) => {
      const existing = byName.get(assignment.tool_name);
      if (existing) {
        existing.assignment = assignment;
      } else {
        // Assigned tool not in available list — synthesise a minimal Tool.
        byName.set(assignment.tool_name, {
          tool: {
            name: assignment.tool_name,
            description: '(Tool not currently registered)',
            category: 'unknown',
            dangerous: false,
            input_schema: { type: 'object', properties: {}, required: [] },
          },
          assignment,
        });
      }
    });
    return Array.from(byName.values()).sort((a, b) => a.tool.name.localeCompare(b.tool.name));
  }, [data]);

  /**
   * All distinct categories present in the current tool list, with counts
   * and friendly metadata, bucketed by group.
   */
  const categoryIndex = useMemo(() => {
    const counts = new Map<string, number>();
    rows.forEach((r) => {
      const id = (r.tool.category || 'unknown').toLowerCase();
      counts.set(id, (counts.get(id) || 0) + 1);
    });
    const entries: (ToolCategoryMeta & { count: number })[] = [];
    counts.forEach((count, id) => {
      entries.push({ ...getToolCategoryMeta(id), count });
    });
    entries.sort((a, b) => a.label.localeCompare(b.label));
    const grouped = new Map<ToolCategoryGroup, (ToolCategoryMeta & { count: number })[]>();
    entries.forEach((entry) => {
      if (!grouped.has(entry.group)) grouped.set(entry.group, []);
      grouped.get(entry.group)!.push(entry);
    });
    // Order groups by their declared `order`.
    const orderedGroups = Array.from(grouped.entries()).sort(
      (a, b) =>
        (TOOL_CATEGORY_GROUP_META[a[0]]?.order ?? 100) -
        (TOOL_CATEGORY_GROUP_META[b[0]]?.order ?? 100)
    );
    return { entries, orderedGroups };
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter(({ tool, assignment }) => {
      const toolCatId = (tool.category || 'unknown').toLowerCase();
      if (category !== 'all' && toolCatId !== category) return false;
      if (filter === 'assigned' && !assignment) return false;
      if (filter === 'available' && assignment) return false;
      if (filter === 'enabled' && !(assignment && assignment.is_enabled)) return false;
      if (filter === 'disabled' && !(assignment && !assignment.is_enabled)) return false;
      if (filter === 'dangerous' && !tool.dangerous) return false;
      if (needle) {
        const meta = getToolCategoryMeta(tool.category);
        const hay = `${tool.name} ${tool.description} ${tool.category} ${meta.label}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, search, category, filter]);

  /**
   * When no specific category is selected we render tools in category
   * sections so users can scan them by topic.
   */
  const groupedFiltered = useMemo(() => {
    if (category !== 'all') return null;
    const map = new Map<string, { meta: ToolCategoryMeta; rows: ToolRow[] }>();
    filtered.forEach((row) => {
      const meta = getToolCategoryMeta(row.tool.category);
      if (!map.has(meta.id)) map.set(meta.id, { meta, rows: [] });
      map.get(meta.id)!.rows.push(row);
    });
    const sections = Array.from(map.values()).sort((a, b) => {
      const ga = TOOL_CATEGORY_GROUP_META[a.meta.group]?.order ?? 100;
      const gb = TOOL_CATEGORY_GROUP_META[b.meta.group]?.order ?? 100;
      if (ga !== gb) return ga - gb;
      return a.meta.label.localeCompare(b.meta.label);
    });
    return sections;
  }, [filtered, category]);

  const stats = useMemo(() => {
    const assigned = rows.filter((r) => r.assignment);
    const enabled = assigned.filter((r) => r.assignment?.is_enabled);
    const disabled = assigned.filter((r) => !r.assignment?.is_enabled);
    const dangerous = rows.filter((r) => r.tool.dangerous);
    const used = assigned.filter((r) => (r.assignment?.usage_count || 0) > 0);
    return {
      all: rows.length,
      assigned: assigned.length,
      available: rows.length - assigned.length,
      enabled: enabled.length,
      disabled: disabled.length,
      dangerous: dangerous.length,
      used: used.length,
    };
  }, [rows]);

  const handleToggleAssign = (row: ToolRow, next: boolean) => {
    if (next) {
      assignMutation.mutate(
        {
          tool_name: row.tool.name,
          is_enabled: true,
          custom_config: row.assignment?.custom_config || {},
        },
        {
          onSuccess: () => toast.success(`Added ${row.tool.name}`),
        }
      );
    } else {
      if (!window.confirm(`Remove "${row.tool.name}" from this agent?`)) return;
      unassignMutation.mutate(row.tool.name);
    }
  };

  const handleToggleEnabled = (row: ToolRow, next: boolean) => {
    if (!row.assignment) return;
    assignMutation.mutate({
      tool_name: row.tool.name,
      is_enabled: next,
      custom_config: row.assignment.custom_config || {},
    });
  };

  const openConfig = (row: ToolRow) => {
    setConfigTool(row);
    setConfigJson(
      JSON.stringify(row.assignment?.custom_config || {}, null, 2) || '{}'
    );
    setConfigEnabled(row.assignment?.is_enabled ?? true);
  };

  const closeConfig = () => {
    setConfigTool(null);
    setConfigJson('{}');
    setConfigEnabled(true);
  };

  const saveConfig = () => {
    if (!configTool) return;
    let parsed: Record<string, any> = {};
    try {
      parsed = configJson.trim() ? JSON.parse(configJson) : {};
    } catch {
      toast.error('Custom configuration must be valid JSON');
      return;
    }
    assignMutation.mutate(
      {
        tool_name: configTool.tool.name,
        is_enabled: configEnabled,
        custom_config: parsed,
      },
      {
        onSuccess: () => {
          toast.success(`Saved ${configTool.tool.name}`);
          closeConfig();
        },
      }
    );
  };

  const clearFilters = () => {
    setSearch('');
    setCategory('all');
    setFilter('all');
  };

  if (error) {
    return <Alert severity="error">Failed to load tools. Please try again.</Alert>;
  }

  return (
    <Stack spacing={{ xs: 2, sm: 2.5 }}>
      <SectionCard
        title="Tools"
        icon={<ToolIcon fontSize="small" />}
        description={
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
            <Chip size="small" variant="outlined" label={`${stats.assigned} assigned`} />
            <Chip size="small" variant="outlined" label={`${stats.enabled} enabled`} color="success" />
            <Chip
              size="small"
              variant="outlined"
              label={`${stats.available} available to add`}
            />
            {stats.dangerous > 0 ? (
              <Chip
                size="small"
                color="warning"
                icon={<WarningIcon sx={{ fontSize: 16 }} />}
                label={`${stats.dangerous} dangerous`}
              />
            ) : null}
            <Chip size="small" variant="outlined" label={`${stats.used} used so far`} />
          </Stack>
        }
      >
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.25}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Search tools by name, description, or category"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title="Clear filters">
              <span>
                <IconButton
                  onClick={clearFilters}
                  disabled={search === '' && category === 'all' && filter === 'all'}
                  size="small"
                >
                  <ClearFilterIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ rowGap: 1 }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <FilterIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Status
              </Typography>
            </Stack>
            {(
              [
                { id: 'all', label: 'All', count: stats.all },
                { id: 'assigned', label: 'Assigned', count: stats.assigned },
                { id: 'enabled', label: 'Enabled', count: stats.enabled },
                { id: 'disabled', label: 'Disabled', count: stats.disabled },
                { id: 'available', label: 'Available', count: stats.available },
                { id: 'dangerous', label: 'Dangerous', count: stats.dangerous },
              ] as { id: FilterMode; label: string; count: number }[]
            ).map((mode) => (
              <Chip
                key={mode.id}
                label={`${mode.label} (${mode.count})`}
                size="small"
                color={filter === mode.id ? 'primary' : 'default'}
                variant={filter === mode.id ? 'filled' : 'outlined'}
                onClick={() => setFilter(mode.id)}
                disabled={mode.id !== 'all' && mode.count === 0}
                clickable
              />
            ))}
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ rowGap: 1 }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <CategoryIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Category
              </Typography>
            </Stack>
            <Chip
              label={`All categories (${rows.length})`}
              size="small"
              color={category === 'all' ? 'secondary' : 'default'}
              variant={category === 'all' ? 'filled' : 'outlined'}
              onClick={() => setCategory('all')}
            />
            {category !== 'all' ? (
              (() => {
                const activeMeta = getToolCategoryMeta(category);
                const count =
                  categoryIndex.entries.find((e) => e.id === category)?.count || 0;
                return (
                  <Chip
                    icon={React.cloneElement(activeMeta.icon, { fontSize: 'small' })}
                    label={`${activeMeta.label} (${count})`}
                    size="small"
                    color={activeMeta.color}
                    onDelete={() => setCategory('all')}
                  />
                );
              })()
            ) : null}
            <Button
              size="small"
              variant="outlined"
              startIcon={<CategoryIcon />}
              endIcon={<ExpandMoreIcon />}
              onClick={(e) => setCategoryMenuAnchor(e.currentTarget)}
            >
              Browse categories
            </Button>
            <Menu
              anchorEl={categoryMenuAnchor}
              open={Boolean(categoryMenuAnchor)}
              onClose={() => setCategoryMenuAnchor(null)}
              PaperProps={{ sx: { maxHeight: 440, minWidth: 280 } }}
            >
              <MenuItem
                selected={category === 'all'}
                onClick={() => {
                  setCategory('all');
                  setCategoryMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  <CategoryIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="All categories"
                  secondary={`${rows.length} tools total`}
                />
              </MenuItem>
              <Divider />
              {categoryIndex.orderedGroups.map(([groupId, cats]) => {
                const groupMeta = TOOL_CATEGORY_GROUP_META[groupId];
                return [
                  <ListSubheader key={`${groupId}-h`} disableSticky>
                    {groupMeta?.label || groupId}
                  </ListSubheader>,
                  ...cats.map((cat) => (
                    <MenuItem
                      key={cat.id}
                      selected={category === cat.id}
                      onClick={() => {
                        setCategory(cat.id);
                        setCategoryMenuAnchor(null);
                      }}
                    >
                      <ListItemIcon>
                        {React.cloneElement(cat.icon, { fontSize: 'small' })}
                      </ListItemIcon>
                      <ListItemText primary={cat.label} secondary={cat.description} />
                      <Chip
                        size="small"
                        label={cat.count}
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    </MenuItem>
                  )),
                ];
              })}
            </Menu>
          </Stack>
        </Stack>
      </SectionCard>

      {!isLoading && rows.length > 0 ? (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ px: 0.5 }}
        >
          <Typography variant="caption" color="text.secondary">
            Showing <strong>{filtered.length}</strong> of {rows.length} tool
            {rows.length === 1 ? '' : 's'}
          </Typography>
          {(search || category !== 'all' || filter !== 'all') && (
            <>
              <Typography variant="caption" color="text.disabled">
                ·
              </Typography>
              <Button
                size="small"
                startIcon={<ClearFilterIcon />}
                onClick={clearFilters}
                sx={{ py: 0, minHeight: 22 }}
              >
                Reset filters
              </Button>
            </>
          )}
        </Stack>
      ) : null}

      {isLoading ? (
        <Stack spacing={1}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={80} />
          ))}
        </Stack>
      ) : filtered.length === 0 ? (
        <Alert severity="info">
          No tools match the current filters.{' '}
          <Button size="small" onClick={clearFilters}>
            Reset filters
          </Button>
        </Alert>
      ) : (
        (() => {
          const renderCard = (row: ToolRow) => {
            const assigned = Boolean(row.assignment);
            const enabled = row.assignment?.is_enabled ?? false;
            const hasConfig =
              row.assignment &&
              Object.keys(row.assignment.custom_config || {}).length > 0;
            const lastUsed = row.assignment?.last_used
              ? formatStoredDateTime(row.assignment.last_used, undefined, null)
              : null;
            const meta = getToolCategoryMeta(row.tool.category);
            return (
              <Box
                key={row.tool.name}
                sx={{
                  border: 1,
                  borderColor: assigned
                    ? alpha(theme.palette.primary.main, 0.4)
                    : 'divider',
                  borderRadius: 2,
                  p: 1.5,
                  bgcolor: assigned
                    ? alpha(theme.palette.primary.main, 0.04)
                    : 'background.paper',
                  transition: 'border-color 120ms ease, background-color 120ms ease',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ wordBreak: 'break-word' }}
                      >
                        {row.tool.name}
                      </Typography>
                      {row.tool.dangerous ? (
                        <Tooltip title="Dangerous tool — requires confirmation before use">
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            icon={<WarningIcon sx={{ fontSize: 14 }} />}
                            label="Dangerous"
                          />
                        </Tooltip>
                      ) : null}
                      <Tooltip title={meta.description}>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={meta.color}
                          icon={React.cloneElement(meta.icon, { fontSize: 'small' })}
                          label={meta.label}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCategory(meta.id);
                          }}
                        />
                      </Tooltip>
                      {hasConfig ? (
                        <Chip
                          size="small"
                          color="info"
                          variant="outlined"
                          icon={<SettingsIcon sx={{ fontSize: 14 }} />}
                          label="Configured"
                        />
                      ) : null}
                    </Stack>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 0.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {row.tool.description || '—'}
                    </Typography>
                  </Box>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Tooltip title={assigned ? 'Remove from this agent' : 'Add to this agent'}>
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={
                          <Switch
                            size="small"
                            checked={assigned}
                            onChange={(e) => handleToggleAssign(row, e.target.checked)}
                            disabled={assignMutation.isPending || unassignMutation.isPending}
                          />
                        }
                        label={
                          <Typography variant="caption" color="text.secondary">
                            {assigned ? 'Attached' : 'Attach'}
                          </Typography>
                        }
                      />
                    </Tooltip>
                  </Stack>
                </Stack>

                {assigned ? (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                    sx={{ mt: 0.5 }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.25}
                      alignItems="center"
                      flexWrap="wrap"
                      sx={{ rowGap: 0.25 }}
                    >
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={
                          <Switch
                            size="small"
                            color="success"
                            checked={enabled}
                            onChange={(e) => handleToggleEnabled(row, e.target.checked)}
                            disabled={assignMutation.isPending}
                          />
                        }
                        label={
                          <Typography
                            variant="caption"
                            color={enabled ? 'success.main' : 'text.secondary'}
                            fontWeight={600}
                          >
                            {enabled ? 'Enabled' : 'Disabled'}
                          </Typography>
                        }
                      />
                      <Typography variant="caption" color="text.secondary">
                        Used {row.assignment?.usage_count || 0}×
                        {lastUsed ? ` · last ${lastUsed}` : ' · never'}
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      onClick={() => openConfig(row)}
                      startIcon={<SettingsIcon />}
                      variant="text"
                    >
                      Configure
                    </Button>
                  </Stack>
                ) : null}
              </Box>
            );
          };

          const grid = (rowsToRender: ToolRow[]) => (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 1.25,
              }}
            >
              {rowsToRender.map(renderCard)}
            </Box>
          );

          if (!groupedFiltered) return grid(filtered);

          return (
            <Stack spacing={2.5}>
              {groupedFiltered.map((section) => (
                <Box key={section.meta.id}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 1, px: 0.5 }}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color:
                          section.meta.color === 'default'
                            ? 'text.secondary'
                            : `${section.meta.color}.main`,
                      }}
                    >
                      {React.cloneElement(section.meta.icon, { fontSize: 'small' })}
                    </Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {section.meta.label}
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${section.rows.length} tool${section.rows.length === 1 ? '' : 's'}`}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {section.meta.description}
                    </Typography>
                  </Stack>
                  {grid(section.rows)}
                </Box>
              ))}
            </Stack>
          );
        })()
      )}

      <Drawer
        anchor={isSmDown ? 'bottom' : 'right'}
        open={Boolean(configTool)}
        onClose={closeConfig}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 480 },
            maxHeight: { xs: '85vh', sm: '100%' },
          },
        }}
      >
        {configTool ? (
          <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ToolIcon color="primary" />
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    {configTool.tool.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {getToolCategoryMeta(configTool.tool.category).label}
                  </Typography>
                </Box>
              </Stack>
              <IconButton onClick={closeConfig} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Stack>

            <Typography variant="body2" sx={{ mb: 2 }}>
              {configTool.tool.description || 'No description.'}
            </Typography>

            {configTool.tool.dangerous ? (
              <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                This tool can perform destructive or sensitive operations. Confirmations are required
                before execution.
              </Alert>
            ) : null}

            <FormControlLabel
              control={
                <Switch
                  checked={configEnabled}
                  onChange={(e) => setConfigEnabled(e.target.checked)}
                  color="success"
                />
              }
              label={configEnabled ? 'Enabled for this agent' : 'Disabled'}
            />

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Input schema
            </Typography>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                bgcolor: alpha(theme.palette.background.default, 0.6),
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                maxHeight: 160,
                overflow: 'auto',
              }}
            >
              {Object.keys(configTool.tool.input_schema?.properties || {}).length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  No parameters declared.
                </Typography>
              ) : (
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {Object.entries(configTool.tool.input_schema.properties).map(([key, value]: any) => (
                    <li key={key}>
                      <strong>{key}</strong>: {value.type}
                      {configTool.tool.input_schema.required?.includes(key) ? (
                        <Typography component="span" variant="caption" color="warning.main">
                          {' '}
                          (required)
                        </Typography>
                      ) : null}
                    </li>
                  ))}
                </Box>
              )}
            </Box>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Custom configuration (JSON)
            </Typography>
            <TextField
              multiline
              fullWidth
              rows={8}
              value={configJson}
              onChange={(e) => setConfigJson(e.target.value)}
              placeholder={'{\n  "key": "value"\n}'}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
            />
            <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 1 }}>
              Configuration is passed to the tool at runtime. Leave empty for defaults.
            </Alert>

            <Stack direction="row" spacing={1} sx={{ mt: 'auto', pt: 2, justifyContent: 'flex-end' }}>
              <Button onClick={closeConfig}>Cancel</Button>
              <Button
                variant="contained"
                startIcon={<CheckIcon />}
                onClick={saveConfig}
                disabled={assignMutation.isPending}
              >
                Save
              </Button>
            </Stack>
          </Box>
        ) : null}
      </Drawer>
    </Stack>
  );
};

export default ToolsTab;
